import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";

import type { ApartmentType } from "@stepapp/core/apiTypes";
import { Block, SvgIcon, Touchable, Typography, palette, spacing } from "@stepapp/ui";

type Props = {
  propertyId: string;
  selectItem: (propertyId: string) => void;
  editButtonPress?: () => void;
  selected: boolean;
  city: string;
  postalOrZipCode: string;
  streetAddress1: string;
  streetAddress2: string;
  floorArea: number;
  homeType: ApartmentType;
  showSecondLineAddress?: boolean;
  name: string;
};

const PropertyItem = ({
  propertyId,
  selectItem,
  editButtonPress,
  selected,
  city,
  postalOrZipCode,
  streetAddress1,
  streetAddress2,
  floorArea,
  homeType,
  showSecondLineAddress = true,
  name,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Touchable onPress={() => selectItem(propertyId)} style={styles.itemContainer}>
      <Block alignCenter row padding={3} style={[styles.container, selected && styles.selected]}>
        <Block justifyCenter flex={1}>
          <Typography type={selected ? "white" : "primary"} variant="newH3">
            {name}
          </Typography>

          <Block marginVertical={1}>
            <Typography type={selected ? "white" : "primary"} variant="paragraph">
              {`${streetAddress1} ${streetAddress2}`}
            </Typography>
            {showSecondLineAddress && (
              <Typography type={selected ? "white" : "primary"} variant="paragraph">
                {`${postalOrZipCode} ${city}`}
              </Typography>
            )}
          </Block>
          <Block alignCenter row>
            <Typography
              decoration="capitalize"
              type={selected ? "white" : "primary"}
              variant="lightLabel"
            >
              {t("screens.homeEdit.type", { context: homeType })}
            </Typography>
            <Block style={[styles.dot, selected && styles.darkDot]} />
            <Typography
              type={selected ? "white" : "primary"}
              variant="lightLabel"
            >{`${floorArea}m`}</Typography>
            <Typography
              style={styles.square}
              type={selected ? "white" : "primary"}
              variant="smallParagraph"
            >
              {2}
            </Typography>
          </Block>
        </Block>
      </Block>
      {selected && editButtonPress && (
        <Touchable onPress={() => editButtonPress()} style={styles.editAddressIcon}>
          <Block height={4.5} marginRight={1} style={styles.editAddressIcon} width={4.5}>
            <SvgIcon name="edit" size={20} />
          </Block>
        </Touchable>
      )}
    </Touchable>
  );
};

export default React.memo(PropertyItem);

const styles = StyleSheet.create({
  container: {
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
    minHeight: spacing.unit * 9,
    overflow: "hidden",
    paddingVertical: spacing.unit * 2,
    width: "100%",
  },
  darkDot: {
    backgroundColor: palette.common.white,
  },
  dot: {
    backgroundColor: palette.common.black,
    borderRadius: spacing.unit * 1,
    height: spacing.unit * 0.375,
    marginHorizontal: spacing.unit * 1,
    width: spacing.unit * 0.375,
  },
  editAddressIcon: {
    alignItems: "center",
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius * 2,
    justifyContent: "center",
    position: "absolute",
    right: 0,
    top: spacing.unit * 0.5,
  },
  itemContainer: {
    width: "100%",
  },
  selected: {
    backgroundColor: palette.primary.accent,
  },
  square: {
    marginBottom: spacing.unit * 1,
  },
});
